/**
 * Settings, How strictly agents follow your standards (ADR 0113, R-FN-4, R-FN-8). One choice
 * (Enforce, Warn only, Off) with one dim sentence; behind Advanced, each rule's level (searchable,
 * grouped) and what landing does with branches it cannot vouch for. Admins change it, everyone
 * else reads it. Every change saves at once; a failed save puts the old value back and says why.
 */
import { Ban, Eye, Search, ShieldCheck, SlidersHorizontal, TriangleAlert } from '@orgus/ui/icons';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { StandardsEnforcement, StandardsEnforcementState, StandardsProvenancePolicy, StandardsRuleClass } from '@orgus/protocol';
import { STANDARDS_ENFORCEMENT_DEFAULT } from '@orgus/protocol';
import { Disclosure, EmptyState, ErrorState, IconTile, Input, Pressable, Select, Skeleton, cn, formatInt, motionPresets } from '@orgus/ui';
import { api, apiGet, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { LEVEL_OPTIONS, MODE_MEANING, MODE_OPTIONS, POLICY_OPTIONS, changedCount, enforceableRules, groupRules, levelOf, withLevel } from './enforcement.ts';
import type { EnforceableRule, EnforcementMode } from './enforcement.ts';

const PATH = '/api/standards/enforcement';
const MODE_ICON: Record<EnforcementMode, { icon: ReactNode; tone: 'accent' | 'attention' | 'neutral' }> = {
  enforce: { icon: <ShieldCheck />, tone: 'accent' },
  warn: { icon: <TriangleAlert />, tone: 'attention' },
  off: { icon: <Ban />, tone: 'neutral' },
};

type RulesFile = { rules?: { id: string; level: string; check: string; summary?: string }[] };

/** The Orgus design rules, loaded only when this section opens. */
function useRuleFile(): RulesFile | null {
  const [file, setFile] = useState<RulesFile | null>(null);
  useEffect(() => {
    let live = true;
    import('../../../../../.orgus/design-rules.json')
      .then((m) => live && setFile((m.default ?? m) as RulesFile))
      .catch(() => live && setFile({ rules: [] }));
    return () => {
      live = false;
    };
  }, []);
  return file;
}

function useEnforcement() {
  const mock = useOrgus((s) => s.mode === 'mock');
  const [state, setState] = useState<StandardsEnforcementState | null>(null);
  const [loadError, setLoadError] = useState<UiError | null>(null);
  const [saveError, setSaveError] = useState<UiError | null>(null);
  const [tick, setTick] = useState(0);
  const latest = useRef(state);
  latest.current = state;

  useEffect(() => {
    if (mock) {
      setState({ enforcement: { ...STANDARDS_ENFORCEMENT_DEFAULT, advisory: ['shape/radius'], off: ['content/copy-budget'] }, packVersion: 'sp_demo' });
      return;
    }
    const ctrl = new AbortController();
    setLoadError(null);
    apiGet<StandardsEnforcementState>(PATH, ctrl.signal)
      .then(setState)
      .catch((e) => !ctrl.signal.aborted && setLoadError(toUiError(e)));
    return () => ctrl.abort();
  }, [mock, tick]);

  const save = async (patch: Partial<StandardsEnforcement>) => {
    const before = latest.current;
    if (!before) return;
    setSaveError(null);
    setState({ ...before, enforcement: { ...before.enforcement, ...patch } });
    if (mock) return;
    try {
      setState(await api<StandardsEnforcementState>(PATH, { method: 'PUT', body: patch }));
    } catch (e) {
      setState(before);
      setSaveError(toUiError(e));
    }
  };
  return { state, loadError, saveError, save, retry: () => setTick((t) => t + 1) };
}

/** The one choice, three tiles in a radio group; arrow keys move it. */
function ModeChoice({ value, onChange, disabled }: { value: EnforcementMode; onChange: (m: EnforcementMode) => void; disabled: boolean }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <div role="radiogroup" aria-label="How strictly agents follow your standards" aria-readonly={disabled || undefined} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {MODE_OPTIONS.map((o, i) => {
        const on = o.value === value;
        return (
          <Pressable
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            role="radio"
            aria-checked={on}
            aria-disabled={disabled || undefined}
            tabIndex={on ? 0 : -1}
            onClick={() => !disabled && onChange(o.value)}
            onKeyDown={(e) => {
              const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
              if (!d || disabled) return;
              e.preventDefault();
              const n = (i + d + MODE_OPTIONS.length) % MODE_OPTIONS.length;
              onChange(MODE_OPTIONS[n]!.value);
              refs.current[n]?.focus();
            }}
            className={cn('group/seg relative flex min-w-0 items-center gap-3 rounded-card border border-border bg-surface px-4 py-3 text-left shadow-elevation-flat', disabled && 'cursor-default')}
          >
            {on ? (
              <motion.span layoutId="standards-mode" transition={motionPresets.move} className="selected-tint absolute inset-1 rounded-control" aria-hidden />
            ) : (
              !disabled && <span className="absolute inset-1 rounded-control opacity-0 transition-opacity duration-(--duration-fast) group-hover/seg:bg-hover group-hover/seg:opacity-100" aria-hidden />
            )}
            <IconTile icon={MODE_ICON[o.value].icon} tone={on ? MODE_ICON[o.value].tone : 'neutral'} size="sm" className="relative" />
            <span className="relative flex min-w-0 flex-col">
              <span className={cn('text-ui font-medium', on ? 'text-fg' : 'text-fg-muted')}>
                {o.label}
                {o.value === 'enforce' && <span className="font-normal text-fg-subtle"> (default)</span>}
              </span>
              <span className="truncate text-caption text-fg-subtle">{o.text}</span>
            </span>
          </Pressable>
        );
      })}
    </div>
  );
}

function RuleRow({ rule, level, onLevel, disabled }: { rule: EnforceableRule; level: StandardsRuleClass; onLevel: (l: StandardsRuleClass) => void; disabled: boolean }) {
  return (
    <li className="flex min-h-row min-w-0 items-center gap-3 px-2 py-1">
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-ui text-fg">{rule.id}</span>
        {rule.summary && <span className="truncate text-caption text-fg-subtle">{rule.summary}</span>}
      </span>
      <Select aria-label={`Level for ${rule.id}`} size="sm" value={level} onValueChange={(v) => onLevel(v as StandardsRuleClass)} options={LEVEL_OPTIONS} disabled={disabled} className="w-32 shrink-0" />
    </li>
  );
}

function RuleLevels({ rules, e, onSave, disabled }: { rules: EnforceableRule[] | null; e: StandardsEnforcement; onSave: (p: Partial<StandardsEnforcement>) => void; disabled: boolean }) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => (rules ? groupRules(rules, query) : []), [rules, query]);
  if (!rules) return <Skeleton className="h-32" />;
  if (rules.length === 0) return <EmptyState icon={<ShieldCheck />} title="No rules to check yet" description="Rules appear here once your standards include design rules." />;
  return (
    <div className="flex flex-col gap-3">
      <Input size="sm" icon={<Search />} aria-label="Search rules" placeholder="Search rules" value={query} onChange={(ev) => setQuery(ev.target.value)} />
      {groups.length === 0 ? (
        <p className="px-2 text-caption text-fg-subtle">No rule matches that search.</p>
      ) : (
        groups.map((g) => (
          <section key={g.label} aria-label={g.label} className="flex flex-col">
            <h4 className="px-2 pb-1 text-caption font-medium text-fg-subtle">
              {g.label} <span className="font-normal text-fg-subtle num">{formatInt(g.rules.length)}</span>
            </h4>
            <ul className="flex flex-col">
              {g.rules.map((r) => (
                <RuleRow key={r.id} rule={r} level={levelOf(r, e)} disabled={disabled} onLevel={(l) => onSave(withLevel(e, r, l))} />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

function PolicyField({ label, hint, value, onChange, disabled }: { label: string; hint: string; value: StandardsProvenancePolicy; onChange: (v: StandardsProvenancePolicy) => void; disabled: boolean }) {
  return <Select label={label} hint={hint} value={value} onValueChange={(v) => onChange(v as StandardsProvenancePolicy)} options={POLICY_OPTIONS} disabled={disabled} />;
}

export function EnforcementSettings() {
  const canEdit = useCan('admin');
  const file = useRuleFile();
  const { state, loadError, saveError, save, retry } = useEnforcement();
  const e = state?.enforcement ?? null;
  const rules = useMemo(() => (file ? enforceableRules(file, e) : null), [file, e]);

  if (loadError && !state) return <ErrorState error={loadError} onRetry={retry} />;
  if (!e) return <Skeleton className="h-40" />;
  const readOnly = !canEdit;
  const changed = changedCount(e);

  return (
    <div className="flex max-w-240 flex-col gap-4">
      {readOnly && (
        <p className="flex items-center gap-2 text-caption text-fg-subtle">
          <Eye className="size-4" aria-hidden />
          Only an owner or admin can change this.
        </p>
      )}
      <ModeChoice value={e.mode} onChange={(mode) => save({ mode })} disabled={readOnly} />
      <p className="px-1 text-caption text-fg-subtle">{MODE_MEANING[e.mode]}</p>
      {saveError && <ErrorState variant="inline" error={saveError} />}
      <div className="rounded-card border border-border bg-surface">
        <Disclosure title="Advanced" summary={changed ? `${formatInt(changed)} rules changed` : 'Defaults'} icon={<SlidersHorizontal />}>
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <PolicyField
                label="Work from agents on older standards"
                hint="Their edits were checked against rules that have since changed."
                value={e.staleSessions}
                onChange={(staleSessions) => save({ staleSessions })}
                disabled={readOnly}
              />
              <PolicyField
                label="Work from agents Orgus did not watch"
                hint="Orgus cannot tell whether their edits were checked."
                value={e.unknownSessions}
                onChange={(unknownSessions) => save({ unknownSessions })}
                disabled={readOnly}
              />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="px-2 text-ui font-medium text-fg">Each rule</h3>
              <RuleLevels rules={rules} e={e} onSave={save} disabled={readOnly || e.mode === 'off'} />
            </div>
          </div>
        </Disclosure>
      </div>
    </div>
  );
}
